import { Controller } from "@hotwired/stimulus"

// The approval in progress, held at module scope rather than on a controller
// instance.
//
// An approval spans the time it takes a manager to walk over and type a PIN,
// and the ops channel refreshes the check whenever anything on the order
// moves. Anything anchored to an element — the button that opened the
// approval, or a method patched onto the keypad controller — is gone the
// moment a morph replaces that element, and the approval used to die with it
// in silence: the manager typed a correct PIN and nothing happened.
//
// Nothing below is anchored to the page.
let pending = null

// Registered once, at import, on the window — so it survives every re-render.
window.addEventListener("pin-keypad:entered", async (event) => {
  if (!pending) return

  const { membershipId, pin, keypad } = event.detail
  const modal = document.getElementById("manager-approval-modal")
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content

  const response = await fetch(pending.verifyUrl, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: new URLSearchParams({
      challenge_token: pending.challengeToken,
      membership_id: membershipId,
      pin,
      authenticity_token: csrf || ""
    })
  })

  if (!response.ok) {
    const { error } = await response.json().catch(() => ({ error: "Wrong PIN" }))
    showError(modal, error)
    keypad.pin = ""
    keypad.render()
    return
  }

  const { approval_token } = await response.json()
  const submission = pending.submission
  pending = null
  closeModal(modal)

  // An approval that cannot be submitted says so. A manager who approves a
  // void, sees nothing change and approves again is how one void becomes two.
  if (!submission) {
    showError(modal, "Approved, but the action was lost. Try again.")
    return
  }
  submitApproved(submission, approval_token)
})

function showError(modal, message) {
  const el = modal?.querySelector('[data-testid="manager-approval-error"]')
  if (!el) return
  el.textContent = message
  el.classList.remove("hidden")
}

function closeModal(modal) {
  modal?.classList.add("hidden")
  modal?.classList.remove("flex")
}

// A real form, so Turbo handles the POST → redirect → flash chain natively;
// a raw fetch consumes the session flash on its redirect-follow before any
// re-render gets to read it.
function submitApproved(submission, approvalToken) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content
  const form = document.createElement("form")
  form.method = "post"
  form.action = submission.url
  form.style.display = "none"

  const fields = {
    ...submission.fields,
    "interaction[approval_token]": approvalToken,
    authenticity_token: csrf || ""
  }
  if ((submission.method || "POST").toUpperCase() !== "POST") {
    fields._method = submission.method.toLowerCase()
  }
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input")
    input.name = name
    input.value = value ?? ""
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.requestSubmit()
}

export default class extends Controller {
  static values = {
    action: String,
    targetSgid: String,
    startUrl: String,
    verifyUrl: String
  }

  async start(event) {
    event.preventDefault()
    const trigger = event.currentTarget

    // Ask whoever owns this button what to submit once approved, while the
    // button is still attached and the answer is still readable. Reading it
    // after the PIN found a detached node.
    const collected = { submission: null }
    trigger.dispatchEvent(new CustomEvent("manager-approval:collect", {
      detail: collected,
      bubbles: true
    }))

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const resp = await fetch(this.startUrlValue, {
      method: "POST",
      body: new URLSearchParams({
        action_name: this.actionValue,
        target_sgid: this.targetSgidValue,
        authenticity_token: csrf || ""
      }),
      headers: { Accept: "application/json" }
    })
    if (!resp.ok) {
      console.error("manager-approval start failed", await resp.text())
      return
    }
    const { challenge_token } = await resp.json()

    pending = {
      challengeToken: challenge_token,
      verifyUrl: this.verifyUrlValue,
      submission: collected.submission
    }
    this.openModal()
  }

  openModal() {
    const modal = document.getElementById("manager-approval-modal")
    if (!modal) return

    modal.classList.remove("hidden")
    modal.classList.add("flex")

    const tiles = modal.querySelector('[data-pin-keypad-target="tiles"]')
    const panel = modal.querySelector('[data-pin-keypad-target="panel"]')
    if (tiles) tiles.hidden = false
    if (panel) panel.hidden = true

    modal.querySelector('[data-testid="manager-approval-error"]')?.classList.add("hidden")
  }

  closeModal() {
    pending = null
    closeModal(document.getElementById("manager-approval-modal"))
  }
}
